/**
 * This library provides a set of utilities that are built on top of filter parsing.
 */

import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';
import uniq from 'lodash/uniq';
import escape from 'lodash/escape';
import unescape from 'lodash/unescape';

import * as K from 'common/constants';
import { FacetCompletionInfo, FacetsCompletionInfo, Range, UrlParams } from 'common/types';
import { isNumeric } from 'common/util/number';
import {
  escapeFacetName,
  filterValueToText,
  quote,
  regExEscape,
  textToFilterValue,
  unescapeFacetName,
  unquote,
  wildcardRegex,
} from 'common/util/string';
import { ResultsTab, View, VIEW_NAME } from 'views/types';
import { FIELD_TYPE, SchemaFields } from 'schemas/types';

import {
  AddTermToFilterParams,
  AdjustedQuery,
  Expression,
  EXPRESSION_TYPE,
  FILTER_STATE,
  FilterState,
  Match,
  MatchInfo,
  MaybeNode,
  Node,
  PARSE_TYPE,
  Term,
  Token,
  TOKEN_TYPE,
} from './types';

import {
  CHAR_STATE,
  COMPLETE_EMPTY_STRING,
  CONSTRAINING_OPERATORS,
  FILTER_STATE_NEXT,
  GRAPH_FUNCTIONS,
  INVERSE,
  TOKEN_CLASS,
  WORDS,
} from './constants';

import {
  findExpressions,
  findIndexOfLastToken,
  findTerms,
  isComplexExpression,
  isPowerQuery,
  parse,
  serialize,
  tokenizeFilter,
} from './parse';

import { tokenizePowerQuery } from './command';

/**
 * Return the CSS class for the given type of token.
 *
 * @param type    one of the * constants
 *
 * @returns CSS class(es)
 */
export const getTokenClass = (type: string): string => TOKEN_CLASS[type] || 'token-' + type;

/**
 * Convert the given filter text into highlighted text by replacing recognized tokens with SPANs
 * that have the appropriate class based on the token type. Spaces are converted into non-breaking spaces
 * (char 160).
 *
 * @param filter    search string
 *
 * @returns highlighted filter text as HTML
 */
export const highlight = (filter: string): string => {
  if (!filter) {
    return '';
  }

  const tokens = tokenizeFilter(filter);

  const findToken = (idx: number): Token | undefined => tokens.find((t) => idx >= t.start && idx < t.end);

  let html = '';
  let pos = 0;
  while (pos < filter.length) {
    const token = findToken(pos);
    if (token) {
      token.text = escape(token.text);
      token.text = token.text.replace(/\n/g, '\\n');
      const className = getTokenClass(token.type);
      html += '<span class="' + className + '">' + token.text.replace(/ /g, '\xA0') + '</span>';
      pos = token.end;
    } else {
      const ch = filter[pos];
      html += ch === ' ' ? '\xA0' : ch;
      pos += 1;
    }
  }

  return html;
};

// Remove initial $ from field name if present. It's no longer required or useful.
const stripDollar = (field: string): string => (field.startsWith('$') ? field.substring(1) : field);

/**
 * Find the terms that have to be true for the query to be true, and pull them out into a list. This is an
 * approximation that will work a majority of the time. We consider a term to be constraining if:
 *
 *      1. All of its parent boolean operations are ANDs.
 *      2. The comparison operator is not negative (must be =, ==, or 'contains').
 *      3. The term is not negated (preceded by a 'not').
 *
 * One case where this will provide a false negative is:
 *
 *     (A && B) || (A && C)
 *
 * The parent OR will cause it not to recognize A as a constraining term. If the query were refactored into:
 *
 *     A && (B || C)
 *
 * then A will be correctly recognized as a constraining term.
 *
 * If the original filter uses '&&' anywhere to mean AND, then the adjusted filter will use it everywhere. Otherwise,
 * it will be omitted, since adjacent terms default to being joined by AND.
 *
 * @param filter                   a filter
 * @param fieldTypes               only remove terms that are among these types
 * @param fields                   whitelist of fields to remove
 * @param constrainingOperators    optional operator list to override CONSTRAINING_OPERATORS
 *
 * @returns object { terms: terms, filter: filter } where 'filter' is the new filter and 'terms' is a list of constrained terms
 */
export const removeConstrainingTerms = (
  filter = '',
  fieldTypes?: TOKEN_TYPE[],
  fields?: string[],
  constrainingOperators = CONSTRAINING_OPERATORS,
): AdjustedQuery => {
  let ast: MaybeNode = parse(filter);
  if (!ast) {
    return { terms: [], filter };
  }

  // pulls terms out of the tree into a list
  let termNodes: Array<Node> = [];
  const collectTerms = (node: Node): void => {
    if (node.token.parseType === PARSE_TYPE.TERM) {
      termNodes.push(node);
    }
    if (node.child) {
      collectTerms(node.child);
    } else {
      if (node.left) {
        collectTerms(node.left);
      }
      if (node.right) {
        collectTerms(node.right);
      }
    }
  };

  // returns true if the node has a parent that is OR or NOT
  const hasDeconstrainingParent = (node: Node): boolean => {
    let parent = node.parent;
    while (parent) {
      if (parent.token.type === TOKEN_TYPE.NOT || parent.token.type === TOKEN_TYPE.OR) {
        return true;
      }
      parent = parent.parent;
    }
    return false;
  };

  // returns true if the term uses =, ==, or 'contains' for the comparison
  const isConstrained = (term: Term): boolean => constrainingOperators.includes(term.operator);

  // pull all terms out of tree into flat list
  collectTerms(ast);

  // caller has specified what type of terms can be constraining
  if (fieldTypes) {
    termNodes = termNodes.filter((termNode) => fieldTypes.includes(termNode.token.type as TOKEN_TYPE));
  }

  // caller has provided list of fields that can be constraining
  if (fields) {
    const lcFields = fields.map((field) => stripDollar(field.toLowerCase()));
    termNodes = termNodes.filter((termNode) => {
      const term: Term = termNode.token as Term;
      const field = term.type === TOKEN_TYPE.STRING ? '' : stripDollar(term.field);
      return field && lcFields.includes(field.toLowerCase());
    });
  }

  // check term operators and boolean structure to determine if term is constraining
  termNodes = termNodes.filter((termNode) => {
    const term: Term = termNode.token as Term;
    return isConstrained(term) && !hasDeconstrainingParent(termNode);
  });

  // disallow duplicated fields that use equality; multiple instances of 'contains' op is okay if no equality ops
  // for example, 'a' in 'a=x' is constraining, but 'a' in 'a=x && a=y' is not
  const fieldCount: Record<string, number> = {};
  const hasEqualityOp: Record<string, boolean> = {};
  termNodes.forEach((termNode) => {
    const term: Term = termNode.token as Term;
    let field = term.type !== TOKEN_TYPE.STRING && term.field;
    if (field) {
      field = field.toLowerCase();
      fieldCount[field] = fieldCount[field] > 0 ? fieldCount[field] + 1 : 1;
      if (term.operator === '=' || term.operator === '==') {
        hasEqualityOp[field] = true;
      }
    }
  });
  termNodes = termNodes.filter((termNode) => {
    const term: Term = termNode.token as Term;
    if (term.type === TOKEN_TYPE.STRING) {
      return true;
    }
    const field = term.field.toLowerCase();
    return fieldCount[field] === 1 || !hasEqualityOp[field];
  });

  // mark terms for removal
  termNodes.forEach((termNode) => {
    termNode.REMOVE = true;
  });

  let removed = false;

  // delete nodes marked for removal; collapse a node whose left or right child node has been removed so that it
  // becomes the remaining child node
  const collapse = (node: MaybeNode): MaybeNode => {
    if (!node) {
      return node;
    }
    if (node.token.type === TOKEN_TYPE.AND || node.token.type === TOKEN_TYPE.OR) {
      node.left = collapse(node.left);
      node.right = collapse(node.right);
      if (node.left && node.right && node.left.REMOVE && node.right.REMOVE) {
        if (node.parent) {
          node.REMOVE = true;
        } else {
          return null;
        }
      } else if (node.left && node.left.REMOVE) {
        removed = true;
        return node.right;
      } else if (node.right && node.right.REMOVE) {
        removed = true;
        return node.left;
      }
    } else if (node.child && node.token.type === TOKEN_TYPE.NOT) {
      collapse(node.child);
    } else if (node.REMOVE && !node.parent) {
      removed = true;
      return null;
    }

    return node;
  };

  // make multiple passes through the tree until no more nodes need to be removed
  do {
    removed = false;
    ast = collapse(ast);
  } while (removed);

  const terms: Array<Token> = termNodes.map((node) => node.token);

  return { terms, filter: terms.length > 0 ? serialize(ast, !filter.includes('&&')) : filter };
};

/**
 * Return true if the given filter needs to be encapsulated in parentheses before having another term added to it.
 *
 * The logic used to make that determination is that the filter only needs to be parenthesized if it contains a
 * "naked" OR - one that is at the top level and not already parenthesized. Since OR has lowest precedence, any
 * top-level OR will end up at the top of the AST, so that's all we need to check.
 *
 * @param filter    a filter
 *
 * @returns true if the filter should be parenthesized before a term is added to it
 */
export const needsParens = (filter: string): boolean => {
  if (!filter) {
    return false;
  }

  // check if already parenthesized - closing paren must pair with opening one
  if (filter[0] === '(') {
    let pos = 0;
    let count = 1; // we already have opening paren
    while (count > 0 && pos < filter.length) {
      pos += 1;
      const ch = filter[pos];
      if (ch === '(') {
        count += 1;
      } else if (ch === ')') {
        count -= 1;
      }
    }
    // count has gone to 0, so we've found match; see if it is at the end
    if (pos === filter.length - 1) {
      return false;
    }
  }

  const ast = parse(filter);
  if (!ast) {
    return true; // couldn't parse filter; return true to be on safe side
  }

  // check for top-level OR
  return ast.token.type === TOKEN_TYPE.OR;
};

/**
 * Return the negation of the given term. For example, 'value=5' will return 'value!=5'.
 *
 * @param filter    a filter consisting of a single term
 *
 * @returns a filter that is the negation of the given term
 */
export const negateTerm = (filter: string): string => {
  const tokens = findTerms(filter);
  const token = tokens && tokens.length === 1 && tokens[0];
  if (token && token.parseType === PARSE_TYPE.TERM) {
    const term: Term = token as Term;
    if (term.type === TOKEN_TYPE.STRING) {
      return 'not ' + filter;
    }
    if (term.tokens.length === 3) {
      const newOp = INVERSE[term.operator];
      if (newOp) {
        return term.field + ' ' + newOp + ' ' + term.value;
      }
    }
  }

  return 'not (' + filter + ')';
};

/**
 * Given a field and its value, try to determine if it matched the filter. If it did, put the matching portion in
 * a SPAN with the given class. Assumes that the provided string has already been HTML-escaped.
 *
 * This may not handle all possible filters. Any term that matches will be highlighted regardless of whether it's
 * part of a boolean expression with AND or OR. That should be fine. We don't have to worry about negated terms
 * because they won't be part of the result set.
 *
 * @param field         facet name
 * @param str           value of the facet; could have HTML with SmartLinks
 * @param filter        current filter that determines matches
 * @param matchClass    CSS class to apply to matching text
 *
 * @returns new text with the matching part highlighted
 */
export const showMatches = (field: string, str: string, filter: string, matchClass = 'matched'): string => {
  if (!str || !filter) {
    return str;
  }

  // used for noting which text matched
  const ranges: Range[] = [];
  const MAX_RANGES = 100;

  // add highlight SPANs to the text for any part that matches a search term; we need to unquote and unescape the string
  // to do the matching, then add those back after we're done matching
  const isQuoted = str.startsWith("'") && str.endsWith("'");
  const rawStr = unquote(unescape(str)); // must be this order, so that quotes are recognized by unquote()
  const tokens = findTerms(filter).filter((token) => token.parseType === PARSE_TYPE.TERM);
  const terms: Array<Term> = tokens as Array<Term>;
  for (let i = 0; i < terms.length; i += 1) {
    const term = terms[i];
    let termField = term.field;
    let operator = term.operator;
    let value = term.value;
    if (term.type === TOKEN_TYPE.STRING) {
      // term is just a bare string, defaults to looking in message field
      termField = K.FIELD.MESSAGE;
      operator = 'contains';
      if (term.tokens && term.tokens[0] && term.tokens[0].type === 'regex') {
        // bare string regex, eg: $"/images/[\w\/]+\.png"
        operator = 'matches';
        value = value.substring(1);
      }
      value = unquote(value);
    } else if (term.tokens[2] && term.tokens[2].type === TOKEN_TYPE.STRING) {
      value = unquote(value);
    }
    termField = stripDollar(termField); // normalize to field without leading $ (that's how log line data does it)

    // if we're looking at the right term, perform match based on operator
    if (termField === field && typeof value === 'string' && value.length > 0) {
      const valueLc = value.toLowerCase();
      if (operator === 'contains' && rawStr.toLowerCase().includes(valueLc)) {
        const valueEsc = regExEscape(valueLc);
        const regex = new RegExp('(' + valueEsc + ')', 'gi');
        let m;
        while ((m = regex.exec(rawStr)) !== null) {
          ranges.push({ min: m.index, max: m.index + valueLc.length });
        }
      } else if ((operator === '=' || operator === '==') && rawStr.toLowerCase() === valueLc) {
        ranges.push({ min: 0, max: valueLc.length });
      } else if (operator === 'matches') {
        let regex;
        try {
          // don't escape regex characters here since we want them to retain their meaning; user can include parens but they must match
          regex = new RegExp('(' + valueLc + ')', 'gi');
        } catch (e) {
          // ignore exception
        }
        if (regex) {
          let m;
          const numRanges = ranges.length;
          while ((m = regex.exec(rawStr)) !== null) {
            ranges.push({ min: m.index, max: m.index + m[0].length });
            if (ranges.length > numRanges + MAX_RANGES) {
              ranges.length = numRanges;
              break;
            }
          }
        }
      } else if (operator === 'in' && term.tokens && term.tokens[2] && term.tokens[2].type === EXPRESSION_TYPE.LIST) {
        const listExp: Expression = term.tokens[2] as Expression;
        const values = listExp.tokens
          .filter((token) => token.type === TOKEN_TYPE.STRING)
          .map((token) => String(token.text));
        for (let j = 0; j < values.length; j += 1) {
          const valueLc = values[j].toLowerCase();
          if (rawStr.toLowerCase() === unquote(valueLc)) {
            ranges.push({ min: 0, max: valueLc.length });
          }
        }
      }
    }
  }

  // collapse overlapping ranges
  ranges.sort((rangeA: Range, rangeB: Range) => {
    if (rangeA.min === rangeB.min) {
      if (rangeA.max === rangeB.max) {
        return 0;
      }
      return rangeA.max < rangeB.max ? -1 : 1;
    }
    return rangeA.min < rangeB.min ? -1 : 1;
  });
  // start with first range
  const ranges1: Range[] = ranges.length > 0 ? [ranges[0]] : [];
  // for each of remaining ranges, add to ranges1 if it doesn't overlap, otherwise update current range
  for (let r = 1; r < ranges.length; r += 1) {
    const range = ranges[r];
    const curRange = ranges1[ranges1.length - 1];
    if (range.min > curRange.max) {
      ranges1.push(range);
    } else {
      curRange.max = range.max;
    }
  }

  // Use the ranges to re-construct the original string with matched parts in SPANs.
  // We unescaped the original string to do matching. Re-escape the final product, and re-quote it if it was quoted.
  if (ranges1.length > 0) {
    let result = escape(rawStr.substring(0, ranges1[0].min));
    for (let r = 0; r < ranges1.length; r += 1) {
      const range = ranges1[r];
      const nextRange = ranges1[r + 1];
      result += '<span class="' + matchClass + '">';
      result += escape(rawStr.substring(range.min, range.max));
      result += '</span>';
      result += escape(rawStr.substring(range.max, nextRange?.min));
    }
    return isQuoted ? quote(result) : result;
  }

  // no matches found; will happen for facets not in the filter, or for field=*
  return escape(str);
};

/**
 * Create a term out of its components.
 *
 * @param field    facet name
 * @param op       operator
 * @param value    term value (strings are assumed to have been quoted)
 */
const createTerm = (field: string, op: string, value: string): string => {
  const joinChar = op === '=' ? '' : ' ';
  return [escapeFacetName(field), op, value].join(joinChar);
};

/**
 * Add one filter to another. Each one will have parentheses added if necessary (it contains an OR).
 * Complex expressions and Power Queries require special handling since the new filter needs to be added
 * in the right place.
 *
 * @param baseFilter    filter to extend
 * @param newFilter     filter to add
 *
 * @returns the extended filter
 */
export const extendFilter = (baseFilter?: string, newFilter?: string): string => {
  if (!baseFilter || !newFilter) {
    return baseFilter || newFilter || '';
  }

  const combineFilters = (filterA: string, filterB: string): string => {
    if (!filterA || !filterB) {
      return filterA || filterB || '';
    }
    const resultA = needsParens(filterA) ? `(${filterA})` : filterA;
    const resultB = needsParens(filterB) ? `(${filterB})` : filterB;
    // https://scalyr.myjetbrains.com/youtrack/issue/FRONT-7277 - push k8s-* filters to the beginning of the expression
    const isK8SFilter = K.K8S_CONTROLLERS.some((controller) => filterB.indexOf(controller) !== -1);
    return isK8SFilter ? `${resultB} ${resultA}` : `${resultA} ${resultB}`;
  };

  // for a graphing function, add the term to the function body
  if (isComplexExpression(baseFilter)) {
    const tokens = tokenizeFilter(baseFilter);
    // identify function bodies by their parens
    const pairs: Array<{ left: Token; right: Token }> = [];
    let count = 0;
    let leftParen: Token | undefined;
    tokens.forEach((token, index) => {
      const prevToken = tokens[index - 1];
      const one = tokens[index + 1];
      const two = tokens[index + 2];
      if (token.type === TOKEN_TYPE.LEFT_PAREN) {
        if (count === 0 && prevToken?.type === TOKEN_TYPE.FUNCTION) {
          leftParen = token;
          // if the function has a 'where' clause, pretend that the left paren follows it; otherwise it will get enclosed in parens
          // when we try to add the term to it, because it doesn't parse as a filter
          if (one?.type === TOKEN_TYPE.FIELD && two?.text === TOKEN_TYPE.WHERE) {
            leftParen.start = two.end;
            leftParen.end = two.end + 1;
          }
        }
        count += 1;
      } else if (token.type === TOKEN_TYPE.RIGHT_PAREN) {
        count -= 1;
        if (count === 0 && leftParen) {
          pairs.push({ left: leftParen, right: token });
          leftParen = undefined;
        }
      }
    });
    // add the term based on the pairs of parens we found that surround function bodies
    let pos = 0;
    let result = '';
    // there is usually only one pair, but can be more if multiple complex expressions are joined by math operators
    pairs.forEach((pair) => {
      result += baseFilter.substring(pos, pair.left.end);
      result += combineFilters(baseFilter.substring(pair.left.end, pair.right.start), newFilter);
      result += baseFilter.substring(pair.right.start, pair.right.end);
      pos = pair.right.end;
    });
    const end = pairs[pairs.length - 1]?.right?.end || baseFilter.length;
    result += baseFilter.substring(end);

    return result;
  }

  // term needs to be added to filter before command in a PQ
  if (isPowerQuery(baseFilter)) {
    const tokens = tokenizeFilter(baseFilter);
    const pipeTokenIndex = tokens.findIndex((token) => token.type === TOKEN_TYPE.PIPE);
    const filterA = baseFilter.substring(0, tokens[pipeTokenIndex]?.start || 0);
    const endOfFirstLine = baseFilter.substring(tokens[pipeTokenIndex - 1]?.end, tokens[pipeTokenIndex]?.start || 0);

    return (
      combineFilters(filterA.trim(), newFilter) +
      (endOfFirstLine || ' ') +
      baseFilter.substring(tokens[pipeTokenIndex]?.start || 0)
    );
  }

  return combineFilters(baseFilter, newFilter);
};

/**
 * Forms a term from a field, operator, and value, then adds it to the given filter.
 *
 * Assumes that the value has already been quoted if necessary.
 *
 * If the filter is a complex expression (graphing function), we have to be careful about where we add the new term.
 * It needs to be adjacent to any term within a graphing function.
 *
 * If the filter is a power query, we need to add the term before the first command.
 *
 * @param params    an object with the following possible properties:
 *
 *      filter    filter to augment
 *      field     facet name
 *      op        operator
 *      value     term value (strings are assumed to have been quoted)
 *      text      quoted string to add instead of field/op/value
 *      negate    if true, term should be negated
 *
 * @return filter with term added
 */
export const addTermToFilter = (params: AddTermToFilterParams): string => {
  const { filter = '', field, op, value, text, negate = false } = params;

  // if no term to add, return filter as is
  if (!(field && op && value) && !text) {
    return filter;
  }

  let newTerm = '';
  if (text) {
    newTerm = text;
  } else if (field && op && value) {
    newTerm = createTerm(field, op, value);
  }
  if (negate) {
    newTerm = negateTerm(newTerm);
  }

  return extendFilter(filter.trim(), newTerm);
};

/**
 * Given a set of params, move ones that have a corresponding facet (eg 'serverHost' for 'logSource') into the filter.
 *
 * @param params              set of params
 *
 * @return a new set of params with query terms moved into the filter
 */
export const addQueryTermsToFilter = (params: UrlParams): UrlParams => {
  const filter = params[K.URL_PARAM.FILTER] ?? '';

  // In the legacy client, we split Kubernetes controllers into two params. For example, "k8sDeployment=foo" was represented
  // as "k8sKind=k8s-deployment&k8sController=foo". We no longer do that, but we need to support legacy saved searches, links, etc.
  const adjParams = cloneDeep(params);
  const k8sKind = adjParams[K.QUERY_TERM.KUBERNETES_KIND as unknown as K.URL_PARAM];
  const k8sController = adjParams[K.QUERY_TERM.KUBERNETES_CONTROLLER as unknown as K.URL_PARAM];
  if (k8sKind && k8sController) {
    const k8sKindQueryParam = K.FACET_TERM_TO_QUERY_TERM[k8sKind as K.FACET_TERM];
    adjParams[k8sKindQueryParam as unknown as K.URL_PARAM] = k8sController;
    delete adjParams[K.QUERY_TERM.KUBERNETES_KIND as unknown as K.URL_PARAM];
    delete adjParams[K.QUERY_TERM.KUBERNETES_CONTROLLER as unknown as K.URL_PARAM];
  }

  // returns the appropriate term when a value has at least one asterisk
  const getStarTerm = (value: string): Record<string, string> => {
    let op = '=';
    let adjValue = value;
    if (/^\*[^*]+\*$/.test(value)) {
      // string surrounded by * means "contains"
      op = 'contains';
      adjValue = textToFilterValue(value.substring(1, value.length - 1));
    } else if (value !== '*') {
      // string with wildcard becomes a regex for "matches"
      op = 'matches';
      adjValue = textToFilterValue(wildcardRegex(adjValue));
    }

    return { op, adjValue };
  };

  const newParams = cloneDeep(params);
  let newFilter = filter;
  Object.entries(adjParams).forEach(([param, value]) => {
    const facet: string = K.QUERY_TERM_TO_FACET_TERM[param as unknown as K.QUERY_TERM];
    if (facet && value) {
      let adjValue = value;
      let op = '=';
      let text;
      if (value.includes('*')) {
        ({ op, adjValue } = getStarTerm(value));
        // logSource or log with wildcards means we don't know whether search was for K8S stuff, so include it
        if (param === K.URL_PARAM.LOG_SOURCE || param === K.URL_PARAM.LOG) {
          const terms = [];
          terms.push(createTerm(facet, op, adjValue));
          if (param === K.URL_PARAM.LOG_SOURCE) {
            terms.push(createTerm(K.FACET_TERM.KUBERNETES_CLUSTER, op, adjValue));
          } else {
            K.K8S_CONTROLLERS.forEach((kind) => terms.push(createTerm(kind, op, adjValue)));
          }
          text = '(' + terms.join(' || ') + ')';
        }
      } else {
        adjValue = textToFilterValue(value);
      }
      newFilter = addTermToFilter({
        filter: newFilter,
        field: facet,
        op,
        value: adjValue,
        text,
      });
      newParams[param as K.URL_PARAM] = '';
    }
  });

  if (filter !== newFilter) {
    newParams[K.URL_PARAM.FILTER] = newFilter;
    return newParams;
  }

  return params;
};

/**
 * Given a set of params, look at the facets in the filter and move ones that have a corresponding query term
 * (eg 'logSource' for 'serverHost') out of the filter and into the query term.
 *
 * @param params    set of params
 * @return a new set of params with query terms moved out the filter and into query
 */
export const removeQueryTermsFromFilter = (params: UrlParams): UrlParams => {
  const filter = params[K.URL_PARAM.FILTER] || '';
  const result = removeConstrainingTerms(filter, [TOKEN_TYPE.FIELD], Object.values(K.QUERY_TERM_TO_FACET_TERM));

  let queryChanged = false;
  const newParams = cloneDeep(params);
  result.terms.forEach((term) => {
    let field = (term as Term).field;
    if (field) {
      field = stripDollar(field.trim().toLowerCase());
    }
    const operator = (term as Term).operator;
    let value = isString((term as Term).value) ? (term as Term).value : '';

    const queryTerm = K.FACET_TERM_TO_QUERY_TERM[field as unknown as K.FACET_TERM] as K.QUERY_TERM;

    value = filterValueToText(value);
    if (value && operator === 'contains') {
      value = '*' + value + '*';
    }

    newParams[queryTerm as unknown as K.URL_PARAM] = value;

    queryChanged = true;
  });

  if (result.terms.length > 0) {
    newParams[K.URL_PARAM.FILTER] = result.filter;
  }

  return queryChanged ? newParams : params;
};

/**
 * Given a set of params, convert the given param to the corresponding facet in the filter. This function is similar
 * to {@see addQueryTermsToFilter}, but it is generic and it deals with multiple values for a single facet.
 *
 * @param params       set of params
 * @param paramName    param to remove
 * @param facetName    facet to add to the filter
 *
 * @return a new set of params with the given param moved into the filter
 */
export const moveParamToFilter = (params: UrlParams, paramName: K.URL_PARAM, facetName: string): UrlParams => {
  const paramValue = params[paramName];
  // If the param is not found, we can return early with the original input
  if (!paramValue) {
    return params;
  }

  const newParams = cloneDeep(params);
  // The param value is stored as a comma-separated list, which can be converted to an "in" term or an "=" term,
  // depending on the number of values in the list.
  const quotedValues = paramValue.split(',').map((v) => quote(v));
  const numValues = quotedValues.length;
  const op = numValues === 1 ? '=' : 'in';
  const facetValue = numValues === 1 ? quotedValues[0] : `(${quotedValues.join(',')})`;
  newParams[K.URL_PARAM.FILTER] = addTermToFilter({
    filter: params[K.URL_PARAM.FILTER] ?? '',
    field: facetName,
    op,
    value: facetValue,
  });
  newParams[paramName] = '';

  return newParams;
};

/**
 * Given a set of params, look for the provided facet in the filter and move it out of the filter and into the
 * provided param. This function is similar to {@see removeQueryTermsFromFilter}, but it is generic and it
 * deals with multiple values for a single facet.
 *
 * @param params       set of params
 * @param paramName    param to add
 * @param facetName    facet to remove from the filter
 *
 * @return a new set of params with the given param moved out the filter
 */
export const removeParamFromFilter = (params: UrlParams, paramName: K.URL_PARAM, facetName: string): UrlParams => {
  const filter = params[K.URL_PARAM.FILTER] || '';
  const result = removeConstrainingTerms(filter, [TOKEN_TYPE.FIELD], [facetName], ['=', '==', 'in']);

  // If the facet is not found with exactly one matching term, we can return early with the original input
  if (result.terms.length !== 1) {
    return params;
  }

  const [term] = result.terms as [Term];
  let { operator, value } = term;

  // For us to continue, the inputs must be friendly. Value must be a truthy string, and the operator must be one
  // of "in", "=", or "==". Like its sister function, this is a best-effort quality of life function that doesn't
  // need to contemplate every possibility.
  value = isString(value) ? value : '';
  if (!value || !['in', '=', '=='].includes(operator)) {
    return params;
  }

  const newParams = cloneDeep(params);

  // Convert the predicate of an "in" term to a comma-separated quoted list by removing the surrounding "(" and ")"
  if (operator === 'in') {
    value = value.slice(1, -1);
  }
  // Convert the quoted list to an unquoted list
  value = value.split(',').map(unquote).join(',');

  newParams[paramName] = value;
  newParams[K.URL_PARAM.FILTER] = result.filter;

  return newParams;
};

/**
 * Given a set of params with a deconstructed filter and the current search view, reconstruct the params with
 * the complete filter included. A deconstructed filter is distributed among different parts of application
 * state, such as query string params like "logSource" or a view's internal filter. A complete filter is needed
 * when transitioning to many pages or querying the API.
 *
 * @param params    set of params with a deconstructed filter
 * @param view      currently selected search view
 *
 * @return a new set of params with special terms moved into the filter
 */
export const buildParamsWithCompleteFilter = (params: UrlParams, view: View, tab?: ResultsTab | null): UrlParams => {
  if (view.name !== VIEW_NAME.STANDARD) {
    let newParams = cloneDeep(params);
    // If there is a sourceField defined, move any dataSource param to the filter
    if (view.sources?.sourceField) {
      newParams = moveParamToFilter(newParams, K.URL_PARAM.DATA_SOURCE, view.sources.sourceField);
    }
    // If the view has an internal filter, add it to the filter
    newParams.filter = addTermToFilter({
      filter: newParams.filter,
      text: view.filter ?? '',
    });
    if (tab && tab.filter) {
      newParams.filter = addTermToFilter({
        filter: newParams.filter,
        text: tab.filter ?? '',
      });
    }
    return isEqual(params, newParams) ? params : newParams;
  }
  // If this is the standard view, move QUERY_TERM params to the filter
  return addQueryTermsToFilter(params);
};

/**
 * Add the filter from the current view (if there is one) to the filter portion of the given PQ.
 * Add any columns needed for rendering (based on schema).
 *
 * @param powerQuery    raw PQ from input
 * @param view          currently selected search view
 * @param fields        all the field definitions for the view
 *
 * @returns object with new PQ and a list of added columns
 */
export const buildPowerQueryWithCompleteFilter = (
  powerQuery: string,
  view: View,
  fields: SchemaFields,
): { powerQuery: string; addedColumns: string[] } => {
  let newPowerQuery = powerQuery;
  let addedColumns: string[] = [];

  if (view.name !== VIEW_NAME.STANDARD) {
    const tokens = tokenizePowerQuery(powerQuery);
    const pipeToken = tokens.find((token) => token.type === TOKEN_TYPE.PIPE);
    const filter = pipeToken ? powerQuery.substring(0, pipeToken.start) : powerQuery;
    const commands = pipeToken ? powerQuery.substring(pipeToken.start) : '';
    const newFilter = addTermToFilter({
      filter,
      text: view.filter ?? '',
    });

    let newCommands = commands;
    const commandTokens = tokenizePowerQuery(commands);
    const columnsCommandIdx = commandTokens.findIndex(
      (token) => token.type === TOKEN_TYPE.COMMAND && token.text === 'columns',
    );
    if (columnsCommandIdx !== -1) {
      const columnsCommand = commandTokens[columnsCommandIdx];
      let columnsText = commands.substring(columnsCommand.end);
      const nextTokenIdx = commandTokens.findIndex(
        (token, index) =>
          ![TOKEN_TYPE.WORD, TOKEN_TYPE.COMMA].includes(token.type as TOKEN_TYPE) && index > columnsCommandIdx,
      );
      const nextToken = commandTokens[nextTokenIdx];
      if (nextTokenIdx !== -1) {
        columnsText = commands.substring(columnsCommand.end, nextToken.start);
      }
      const columns = columnsText.trim().split(/\s*,\s*/);
      columns.forEach((column) => {
        const related = fields[column]?.relatedFields ?? [];
        related.forEach((rel) => {
          if (!columns.includes(rel)) {
            addedColumns.push(rel);
          }
        });
      });
      const newColumns = columns.concat(addedColumns);
      newCommands =
        commands.substring(0, columnsCommand.start) +
        'columns ' +
        newColumns.join(',') +
        (nextToken ? commands.substring(nextToken.start) : '');
    }

    newPowerQuery = newFilter + newCommands.trim();
  }

  return { powerQuery: newPowerQuery, addedColumns };
};

/**
 * Given a set of params with a complete filter, remove certain constraining terms from the filter based on the
 * current search view. The deconstructed filter terms might be captured as separate params, or they might be
 * captured by the view's internal filter.
 *
 * @param params    set of params with a complete filter
 * @param view      currently selected search view
 *
 * @return a new set of params with special terms moved out of the filter
 */
export const buildParamsWithDeconstructedFilter = (params: UrlParams, view: View): UrlParams => {
  if (view.name !== VIEW_NAME.STANDARD) {
    let newParams = cloneDeep(params);
    // If the view has an internal filter, remove it from the filter
    const categoryField = view.sources?.categoryField;
    if (categoryField && view.filter) {
      newParams.filter = removeConstrainingTerms(params.filter, [TOKEN_TYPE.FIELD], [categoryField]).filter;
    }
    // If there is a sourceField defined, extract the dataSource param from the filter
    if (view.sources?.sourceField) {
      newParams = removeParamFromFilter(newParams, K.URL_PARAM.DATA_SOURCE, view.sources?.sourceField);
    }
    return isEqual(params, newParams) ? params : newParams;
  }
  // If this is the standard view, extract QUERY_TERM params from the filter
  return removeQueryTermsFromFilter(params);
};

/**
 * Return a state object representing what can come next for the given filter.
 *
 * @param filter    a filter string
 *
 * @return FilterState object with state, text that determined state, valid token types that can follow, field, and operator
 */
export const getFilterState = (filter: string): FilterState => {
  if (!filter) {
    const state = FILTER_STATE.START;
    return {
      text: '',
      states: [state],
      valid: FILTER_STATE_NEXT[state],
      field: '',
      op: '',
    };
  }

  const tokens = tokenizeFilter(filter);
  // keep for debugging
  // console.log(tokens.map((t) => t.type).join(' '));
  const last = tokens[tokens.length - 1] || ({} as Token);
  const lastType = last?.type;
  let text = (!last?.complete && last.text) || '';
  const one = tokens[tokens.length - 2]; // one token back
  const two = tokens[tokens.length - 3]; // two tokens back
  const isTerm = !!(one?.type === TOKEN_TYPE.COMPARISON && two?.type === TOKEN_TYPE.FIELD);

  // handle list following 'in' operator
  const inIndex = findIndexOfLastToken(
    tokens,
    (token: Token) => token.type === TOKEN_TYPE.COMPARISON && token.text === 'in',
  );
  const leftParenIndex = findIndexOfLastToken(tokens, (token: Token) => token.type === TOKEN_TYPE.LEFT_PAREN);
  const rightParenIndex = findIndexOfLastToken(tokens, (token: Token) => token.type === TOKEN_TYPE.RIGHT_PAREN);
  const inList = inIndex !== -1 && leftParenIndex === inIndex + 1 && rightParenIndex < leftParenIndex;
  const isCompletedList = inIndex !== -1 && leftParenIndex === inIndex + 1 && rightParenIndex === tokens.length - 1;

  const states = [] as FILTER_STATE[];

  // grab field and op if we have one or two complete parts of a possible term; if the term is complete we don't need them
  let field = '';
  let op = '';
  const listValues = [];
  if (
    lastType === TOKEN_TYPE.FIELD ||
    (lastType === TOKEN_TYPE.WORD && last.complete && one?.type !== TOKEN_TYPE.COMPARISON)
  ) {
    field = last.text;
  } else if (
    (one?.type === TOKEN_TYPE.FIELD || one?.type === TOKEN_TYPE.WORD) &&
    (lastType === TOKEN_TYPE.COMPARISON || lastType === TOKEN_TYPE.WORD || lastType === TOKEN_TYPE.NOT)
  ) {
    // field with a possible operator
    field = one.text;
    if (lastType === TOKEN_TYPE.COMPARISON || lastType === TOKEN_TYPE.NOT) {
      op = last.text;
      // '=' isn't complete (could become '=='), but we want to treat it that way so that values can be shown
      if (op === '=') {
        text = '';
      }
    }
  } else if (inList) {
    op = 'in';
    field = tokens[inIndex - 1]?.text || '';
    if ([TOKEN_TYPE.LEFT_PAREN, TOKEN_TYPE.COMMA].includes(lastType as TOKEN_TYPE)) {
      // set up to match all values for the field
      states.push(FILTER_STATE.LIST);
      text = '';
    }
    for (let i = leftParenIndex; i < tokens.length; i += 1) {
      // remember values we've seen so we don't offer them as matches
      const token = tokens[i];
      if ([TOKEN_TYPE.STRING, TOKEN_TYPE.NUMBER].includes(token.type as TOKEN_TYPE) && token.complete) {
        listValues.push(unquote(token.text));
      }
    }
  } else if (isTerm && !last.complete) {
    // term where value is not complete; we'll need field to offer matches for value
    field = two.text;
    op = one.text;
  }

  if (lastType === TOKEN_TYPE.FIELD || lastType === TOKEN_TYPE.WORD) {
    // bare word, possibly complete
    if (last.complete) {
      if (last.text.toLowerCase() === TOKEN_TYPE.WHERE) {
        states.push(FILTER_STATE.WHERE);
      } else {
        states.push(FILTER_STATE.WORD, FILTER_STATE.TERM);
        if (one?.type !== TOKEN_TYPE.COMPARISON) {
          states.push(FILTER_STATE.FIELD);
        }
      }
    } else if (one?.type === TOKEN_TYPE.COMPARISON) {
      // boolean values don't require quotes
      states.push(FILTER_STATE.IN_WORD_VALUE);
    } else {
      states.push(FILTER_STATE.IN_WORD);
      if (field && one?.type === TOKEN_TYPE.WORD) {
        // set up for matching text comparators 'contains', 'matches', and 'in'
        states.push(FILTER_STATE.IN_COMPARATOR);
        op = last.text;
      }
    }
    if (tokens.length === 1 && !last.complete) {
      states.push(FILTER_STATE.IN_WORD_START); // good to know because functions have to be at the start
    }
    // look for state where user can add 'where' keyword in a function body
    if (
      isComplexExpression(filter) &&
      lastType === TOKEN_TYPE.WORD &&
      (one?.type === TOKEN_TYPE.LEFT_PAREN || (two?.type === TOKEN_TYPE.LEFT_PAREN && !last.complete))
    ) {
      states.push(FILTER_STATE.FIELD_IN_FUNCTION);
    }
  } else if (lastType === TOKEN_TYPE.COMPARISON || (!!field && lastType === TOKEN_TYPE.NOT)) {
    if (WORDS[TOKEN_TYPE.COMPARISON].includes(last.text.toLowerCase())) {
      if (last.complete) {
        states.push(FILTER_STATE.COMPARATOR, [FILTER_STATE.COMPARATOR, last.text].join('-') as FILTER_STATE);
      } else {
        states.push(FILTER_STATE.IN_COMPARATOR, FILTER_STATE.IN_WORD);
      }
    } else {
      states.push(FILTER_STATE.COMPARATOR);
      if (['!', '<', '>'].includes(last.text) && !last.complete) {
        // don't offer to complete = to ==
        states.push(FILTER_STATE.IN_COMPARATOR);
      }
      if (['=', '==', '!='].includes(last.text)) {
        states.push(FILTER_STATE.COMPARATOR_EQUALITY);
      } else {
        states.push(FILTER_STATE.COMPARATOR_NUMERIC);
      }
    }
  } else if (lastType === TOKEN_TYPE.STRING) {
    if (last.complete || isCompletedList) {
      states.push(FILTER_STATE.STRING, FILTER_STATE.TERM);
    } else {
      states.push(isTerm || inList ? FILTER_STATE.IN_STRING_VALUE : FILTER_STATE.IN_STRING);
    }
  } else if (lastType === TOKEN_TYPE.NUMBER) {
    if (last.complete || isCompletedList) {
      states.push(FILTER_STATE.NUMBER);
      if (isTerm || isCompletedList) {
        states.push(FILTER_STATE.TERM);
      }
    } else {
      states.push(isTerm || inList ? FILTER_STATE.IN_NUMBER_VALUE : FILTER_STATE.IN_NUMBER);
    }
  } else if (lastType === TOKEN_TYPE.INTERVAL && one?.type === TOKEN_TYPE.TIMESHIFT) {
    states.push(FILTER_STATE.TIMESHIFT_INTERVAL);
  } else if (lastType === TOKEN_TYPE.COLON && one?.type === TOKEN_TYPE.COMPARISON) {
    states.push(FILTER_STATE.MODIFIED_COMPARATOR);
    text = '';
  }

  // analyze any non-token text at the end of the filter
  const rest = filter.substring(last ? last.end : 0).trim();
  rest.split('').forEach((c) => {
    if (CHAR_STATE[c]) {
      states.push(CHAR_STATE[c]);
    }
  });

  const validTypes = uniq(
    states.reduce(
      (acc, state) => (FILTER_STATE_NEXT[state] ? acc.concat(FILTER_STATE_NEXT[state]) : acc),
      [] as TOKEN_TYPE[],
    ),
  );

  return {
    states,
    tokens,
    listValues,
    field,
    op,
    text: (rest || text).trim(),
    valid: validTypes,
  } as FilterState;
};

/**
 * Return autocomplete matches for the given type. Handles types that appear in regular search filters.
 *
 * @param type           type of token to match
 * @param filterState    object with state used for matching
 * @param facetInfo      object with facet names and values
 *
 * @return list of matches
 */
export const getFilterMatchesByType = (
  type: TOKEN_TYPE,
  filterState: FilterState,
  facetInfo?: FacetsCompletionInfo,
): Match[] => {
  const field = unescapeFacetName(stripDollar(filterState.field));
  const info = facetInfo?.[field] || ({} as FacetCompletionInfo);
  const isNumericField = info.type === FIELD_TYPE.NUMBER;
  const isBooleanField = info.type === FIELD_TYPE.BOOLEAN;

  // first, adjust the text we use for matching
  let matchText = filterState.text; // text used for matching
  if (type === TOKEN_TYPE.FIELD) {
    matchText = stripDollar(matchText); // remove opening $ in case this is a system field
  } else if (type === TOKEN_TYPE.VALUE && ["'", '"'].includes(filterState.text[0])) {
    matchText = filterState.text.substring(1); // remove opening quote
  } else if (type === TOKEN_TYPE.COMPARISON) {
    matchText = filterState.op || ''; // match against op since text may be empty
  }

  // check for empty match text; if we match against it, all values will be returned
  if (!matchText && !COMPLETE_EMPTY_STRING[type]) {
    return [];
  }

  matchText = matchText.toLowerCase(); // matching is case-insensitive

  // match by type
  if (type === TOKEN_TYPE.FIELD && facetInfo) {
    // match facet names
    const facets = Object.keys(facetInfo);
    return facets
      .filter((facet) => facet.toLowerCase().indexOf(matchText) === 0)
      .filter((facet) => !filterState.isNumeric || facetInfo[facet].type === FIELD_TYPE.NUMBER)
      .filter((facet) => !filterState.isString || facetInfo[facet].type !== FIELD_TYPE.NUMBER)
      .map((facet) => ({ type, text: facet, completionValue: escapeFacetName(facet) + ' ' }));
  }

  if (type === TOKEN_TYPE.FUNCTION) {
    // match graphing functions
    return GRAPH_FUNCTIONS.filter((func) => func.toLowerCase().indexOf(matchText) === 0).map((func) => ({
      type,
      text: func + '()',
      completionValue: func + '(',
    }));
  }

  if (type === TOKEN_TYPE.WHERE) {
    if (TOKEN_TYPE.WHERE.indexOf(matchText.toLowerCase()) === 0) {
      return [{ type, text: TOKEN_TYPE.WHERE, completionValue: TOKEN_TYPE.WHERE + ' ' }];
    }
  }

  if (type === TOKEN_TYPE.TIMESHIFT) {
    if (TOKEN_TYPE.TIMESHIFT.indexOf(matchText.toLowerCase()) === 0) {
      return [{ type, text: TOKEN_TYPE.TIMESHIFT, completionValue: TOKEN_TYPE.TIMESHIFT + ' ' }];
    }
  }

  if (type === TOKEN_TYPE.VALUE) {
    // match facet values if doing equality check for a facet
    const isEquality = filterState.op && ['=', '==', '!=', 'in'].includes(filterState.op);
    if (filterState.field && isEquality) {
      return (info.values || [])
        .filter((value) => value.toLowerCase().indexOf(matchText) === 0 && !filterState.listValues?.includes(value))
        .map((value) => ({
          type,
          text: value,
          completionValue: isNumericField && isNumeric(value) ? value + ' ' : quote(value) + ' ',
        }));
    }
  }

  if (type === TOKEN_TYPE.BOOLEAN) {
    // match facet values if doing equality check for a facet
    const isEquality = filterState.op && ['=', '==', '!='].includes(filterState.op);
    if (filterState.field && isEquality) {
      if (isBooleanField) {
        return WORDS[type]
          .filter((value) => value.indexOf(matchText) === 0)
          .map((value) => ({ type: TOKEN_TYPE.BOOLEAN, text: value, completionValue: value + ' ' }));
      }
    }
  }

  if (type === TOKEN_TYPE.COMPARISON) {
    // create set of operators based on whether field is numeric
    let operators = ['=', '!=', 'in'];
    operators = operators.concat(isNumericField ? ['>', '>=', '<', '<='] : ['contains', 'matches']);
    return operators
      .filter((comp) => comp.indexOf(matchText.toLowerCase()) === 0)
      .map((comp) => {
        let append = ' ';
        if (comp === 'in') {
          append = !isNumericField && !isBooleanField ? " ('" : ' (';
        } else if (['contains', 'matches'].includes(comp) || (!isNumericField && !isBooleanField)) {
          append = " '";
        }
        return { type, text: comp, completionValue: comp + append };
      });
  }

  if (type === TOKEN_TYPE.MODIFIER) {
    return WORDS[type]
      .filter((mod) => mod.indexOf(matchText.toLowerCase()) === 0)
      .map((mod) => ({ type, text: mod, completionValue: mod + ' ' }));
  }

  return [];
};

/**
 * Perform autocomplete matching at the end of the given filter.
 *
 * @param filter       filter to complete
 * @param facetInfo    object with facet names and values
 * @param exclude      token types to exclude from matching
 *
 * @return MatchInfo with matches by type and matched text
 */
export const getFilterMatches = (
  filter: string,
  facetInfo?: FacetsCompletionInfo,
  exclude = [] as TOKEN_TYPE[],
): MatchInfo => {
  const filterState = getFilterState(filter);
  const validTypes = filterState.valid || [];

  // handle partially completed op; text to be replaced will be in 'op' and not 'text'
  let matchedText = filterState.text; // text replaced by a selected match
  if (validTypes.length === 1 && validTypes[0] === TOKEN_TYPE.COMPARISON) {
    matchedText = filterState.op || '';
  }

  const result = {
    matchedText,
    matches: {} as Record<TOKEN_TYPE, Match[]>,
  } as MatchInfo;

  // for each type that could come next, provide a list of possible values based on matching
  validTypes
    .filter((type) => !exclude.includes(type))
    .forEach((type) => {
      result.matches[type] = getFilterMatchesByType(type, filterState, facetInfo);
    });

  return result;
};

/**
 * If the given filter is a PQ or a complex expression (graphing function), convert it to a simple filter.
 * For a PQ, we strip the commands. For a complex expression, we return the filter used by the first
 * graphing function in the filter. If the filter is neither a PQ nor a complex expression, it is returned
 * as is.
 *
 * @param filter    a filter
 *
 * @return simple filter, with no commands or functions
 *
 * TODO: how do we handle multiple expressions, eg 'count(message contains "error") / count(message contains "success")'
 */
export const simplifyFilter = (filter: string): string => {
  if (!filter) {
    return '';
  }

  const tokens = tokenizeFilter(filter);
  if (isPowerQuery(filter)) {
    const pipeIndex = tokens.findIndex((token) => token.type === TOKEN_TYPE.PIPE);
    if (pipeIndex !== -1) {
      return filter.substring(0, tokens[pipeIndex].start).trim();
    }
  }

  if (isComplexExpression(filter)) {
    const expression = findExpressions(tokens).find((expr) => expr.parseType === PARSE_TYPE.EXPRESSION) as Expression;
    let exprTokens = expression?.tokens?.slice(2, expression.tokens.length - 1) || [];
    if (exprTokens.length > 1 && exprTokens[1].type === TOKEN_TYPE.WHERE) {
      exprTokens = exprTokens.slice(2);
    }
    if (exprTokens.length > 0) {
      return filter.substring(exprTokens[0].start, exprTokens[exprTokens.length - 1].end).trim();
    }
  }

  return filter;
};
